import { useState, useEffect } from 'react';
import { Check, ArrowRight, Lock } from 'lucide-react';
import { calculatePricing } from '../data/coachingData';
import { detectVisitorMarket, type MarketCode } from '../services/countryDetectionService';
import {
  subscribeToRegionalPricing,
  fetchPublishedRegionalCatalog,
  type PublishedRegionalCatalog,
} from '../services/pricingService';
import type { CommitmentDuration, CoachingPlan, CommitmentOption } from '../types';

interface PricingPlansProps {
  onSelectPlan: (planId: string, marketContext?: { marketCode: string; versionId?: string }) => void;
}

export const PricingPlans = ({ onSelectPlan }: PricingPlansProps) => {
  const [billingCycle, setBillingCycle] = useState<CommitmentDuration>(3);
  const [loading, setLoading] = useState(true);
  const [detectedMarket, setDetectedMarket] = useState<MarketCode>('US');
  const [catalog, setCatalog] = useState<PublishedRegionalCatalog | null>(null);

  useEffect(() => {
    let isMounted = true;
    let unsubscribe: (() => void) | null = null;

    async function initRegionalPricing() {
      try {
        const detection = await detectVisitorMarket();
        if (!isMounted) return;

        setDetectedMarket(detection.marketCode);

        // Fetch initial catalog
        const initial = await fetchPublishedRegionalCatalog(
          detection.marketCode === 'UNSUPPORTED' ? 'US' : detection.marketCode
        );
        if (isMounted) {
          setCatalog(initial);
          setLoading(false);
        }

        // Real-time listener for market updates
        if (detection.marketCode !== 'UNSUPPORTED') {
          unsubscribe = subscribeToRegionalPricing(detection.marketCode, (updated) => {
            if (isMounted) {
              setCatalog(updated);
            }
          });
        }
      } catch {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    initRegionalPricing();

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const plans: CoachingPlan[] = catalog?.plans || [];
  const commitmentOptions: CommitmentOption[] = catalog?.commitmentOptions || [];
  const currency = catalog?.currency || 'USD';
  const currencySymbol = currency === 'INR' ? '₹' : '$';

  return (
    <section id="pricing" className="light-section pricing-editorial">
      <div className="container">
        <div className="pricing-heading">
          <div>
            <p className="eyebrow">COACHING TIERS &amp; DELIVERABLES</p>
            <h2>
              The right support.<br />
              <em>Your next chapter.</em>
            </h2>
          </div>
          <p>
            Train with a group, work one-to-one, or start together. Explore curriculum inclusions and commitment savings below—exact rates are unlocked following your consultation.
          </p>
        </div>

        {/* Loading Skeleton */}
        {loading && (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#07132f' }}>
            <div style={{ fontSize: '28px', marginBottom: '10px' }}>⏳</div>
            <p style={{ fontWeight: 700, fontSize: '15px' }}>Retrieving coaching programs...</p>
          </div>
        )}

        {/* Interactive Plans Matrix with Masked Price Numbers */}
        {!loading && plans.length > 0 && (
          <>
            <div className="billing-row">
              <span>Choose your commitment tier</span>
              <div className="billing-switch" role="group" aria-label="Billing commitment selector">
                {commitmentOptions.map((option) => (
                  <button
                    key={option.months}
                    type="button"
                    aria-pressed={billingCycle === option.months}
                    onClick={() => setBillingCycle(option.months as CommitmentDuration)}
                  >
                    {option.label}
                    {option.discountPercentage > 0 && <small>Save {option.discountPercentage}%</small>}
                  </button>
                ))}
              </div>
            </div>

            <div id="plans" className="plan-grid">
              {plans.map((plan) => {
                const pricing = calculatePricing(plan.baseMonthlyPrice, billingCycle, commitmentOptions, currency);
                return (
                  <article key={plan.id} className={`plan-card ${plan.featured ? 'plan-featured' : ''}`}>
                    <div className="plan-label">{plan.featured ? '⭐ Most Popular' : 'EVERFORGE COACHING'}</div>
                    <h3>{plan.name}</h3>
                    <p className="plan-tagline">{plan.tagline}</p>

                    {/* Masked Pricing Display with Elegant Consultation Badge */}
                    <div className="plan-price" style={{ alignItems: 'center', gap: '8px' }}>
                      <strong
                        style={{
                          fontSize: '32px',
                          letterSpacing: '2px',
                          color: '#07132f',
                          fontFamily: 'monospace',
                        }}
                        title="Bespoke consultation pricing"
                      >
                        {currencySymbol} •••
                      </strong>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 700,
                          color: '#64748b',
                          textTransform: 'uppercase',
                        }}
                      >
                        / month
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: plan.featured ? '#fef3c7' : '#f1f5f9',
                        color: plan.featured ? '#92400e' : '#475569',
                        border: plan.featured ? '1px solid #fde68a' : '1px solid #cbd5e1',
                        borderRadius: '20px',
                        padding: '4px 10px',
                        fontSize: '11px',
                        fontWeight: 700,
                        margin: '10px 0 14px',
                      }}
                    >
                      <Lock size={12} />
                      <span>Unlocked via Consultation</span>
                    </div>

                    <p className="plan-total" style={{ color: '#475569', fontSize: '12px', marginTop: '2px' }}>
                      <strong>{billingCycle} {billingCycle === 1 ? 'Month' : 'Months'} Term</strong> · Bespoke schedule
                    </p>

                    <p className="plan-saving" style={{ minHeight: '20px', marginBottom: '18px' }}>
                      {pricing.discountPercentage > 0
                        ? `⚡ Unlocks ${pricing.discountPercentage}% commitment savings`
                        : 'Standard monthly terms'}
                    </p>

                    <button
                      className={plan.featured ? 'btn-primary' : 'btn-secondary'}
                      onClick={() =>
                        onSelectPlan(plan.id, {
                          marketCode: detectedMarket,
                          versionId: catalog?.versionId,
                        })
                      }
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontWeight: 800,
                        fontSize: '12px',
                        padding: '12px 14px',
                      }}
                    >
                      <span>Apply For {plan.name}</span>
                      <ArrowRight size={15} />
                    </button>

                    <ul className="plan-highlights">
                      {plan.features.slice(0, 3).map((feature) => (
                        <li key={feature}>
                          <Check size={15} aria-hidden="true" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <details className="plan-details">
                      <summary>Everything included</summary>
                      <ul>
                        {plan.features.slice(3).map((feature) => (
                          <li key={feature}>{feature}</li>
                        ))}
                      </ul>
                      <dl>
                        <dt>Programming</dt>
                        <dd>{plan.deliverables.programming}</dd>
                        <dt>Nutrition</dt>
                        <dd>{plan.deliverables.nutrition}</dd>
                        <dt>Check-ins</dt>
                        <dd>{plan.deliverables.checkIns}</dd>
                        <dt>Form feedback</dt>
                        <dd>{plan.deliverables.videoAnalysis}</dd>
                        <dt>Messaging</dt>
                        <dd>{plan.deliverables.messagingAccess}</dd>
                      </dl>
                    </details>
                  </article>
                );
              })}
            </div>

            <p className="pricing-terms" style={{ color: '#64748b', fontSize: '11.5px', marginTop: '28px' }}>
              🔒 <strong>Private Offer Transparency:</strong> Everforge programs are customized based on training age, medical evaluation, and coaching intensity. Exact monthly rates and commitment savings are reviewed during your consultation and delivered via a secure private proposal before any enrollment or payment.
            </p>
          </>
        )}
      </div>
    </section>
  );
};

export default PricingPlans;
